import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config/api";
import {
  TrendingUp, TrendingDown, MinusCircle, Sparkles, Target, ChevronDown, ChevronRight,
  Activity, BarChart3, Layers, Newspaper, Coins, Banknote,
  MessageSquare, Landmark, FileText, Vote,
} from "lucide-react";
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from "recharts";
import PoliticianBuyersWidget from "./PoliticianBuyersWidget";

/**
 * StockPimp Predicts — unified next-day prediction fusing 14 backtested
 * historical signals + 6 live overlays (futures, AH/PM, news, sector breadth,
 * 10Y treasury, commodities), per the All.pdf master playbook.
 */
export default function StockPimpPredicts({ ticker }) {
  const [data, setData] = useState(null);
  const [edgeData, setEdgeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [window, setWindow] = useState("10y");
  const [horizon, setHorizon] = useState("1d");
  const [threshold, setThreshold] = useState(0.15);
  const [showLive, setShowLive] = useState(true);
  const [showRules, setShowRules] = useState(false);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [predRes, edgeRes] = await Promise.all([
          axios.get(
            `${API}/stock/stockpimp-predicts/${ticker}?window=${window}&horizon=${horizon}&threshold=${threshold}`,
            { timeout: 120000 }
          ),
          axios.get(
            `${API}/stock/stockpimp-predicts/${ticker}/edge-comparison?horizon=${horizon}&threshold=${threshold}`,
            { timeout: 120000 }
          ).catch(() => ({ data: null })),
        ]);
        if (!cancelled) {
          setData(predRes.data);
          setEdgeData(edgeRes.data);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load StockPimp Predicts");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ticker, window, horizon, threshold]);

  if (loading) {
    return (
      <div className="rounded border border-purple-900/60 bg-gradient-to-br from-purple-950/40 to-fuchsia-950/30 p-6 text-center" data-testid="sp-predicts-loading">
        <Sparkles className="w-8 h-8 text-fuchsia-400 mx-auto animate-pulse" />
        <div className="mt-2 text-sm text-gray-300">
          Fusing 25 signals across {ticker} history…
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-3 space-y-2" data-testid="sp-predicts-skeleton">
        <div className="flex items-center gap-2 mb-1">
          <span className="inline-block w-2 h-2 rounded-full bg-amber-400/60 animate-pulse" />
          <span className="text-[11px] text-amber-300/80">Refreshing StockPimp predictions…</span>
        </div>
        <div className="h-32 bg-slate-800/40 rounded animate-pulse" />
      </div>
    );
  }
  if (!data) return null;

  const today = data.today || {};
  const bt = data.backtest || {};
  const ov = data.live_overlays || {};
  const hist = (data.breakdown || []).filter((b) => b.category === "historical");
  const live = (data.breakdown || []).filter((b) => b.category === "live");
  const dir = today.direction || "NEUTRAL";
  const dirColor = dir === "UP" ? "text-emerald-400" : dir === "DOWN" ? "text-red-400" : "text-gray-300";
  const dirBg = dir === "UP" ? "from-emerald-950/60 to-emerald-900/30 border-emerald-700/50"
    : dir === "DOWN" ? "from-red-950/60 to-red-900/30 border-red-700/50"
    : "from-gray-900/60 to-gray-800/30 border-gray-700/50";
  const DirIcon = dir === "UP" ? TrendingUp : dir === "DOWN" ? TrendingDown : MinusCircle;

  const histChart = hist.map((h) => ({
    name: h.label.length > 14 ? h.label.slice(0, 14) + "…" : h.label,
    score: h.score ?? 0,
    weighted: h.weighted ?? 0,
  }));

  const histAcc = bt.history.map((h, i) => ({
    idx: i + 1,
    label: h.date,
    prob: h.composite,
    correct: h.correct,
    direction: h.predicted,
  }));

  const accuracy = bt.accuracy != null ? (bt.accuracy * 100).toFixed(2) : "—";
  const ciLow = bt.ci_95_low != null ? (bt.ci_95_low * 100).toFixed(1) : "—";
  const ciHigh = bt.ci_95_high != null ? (bt.ci_95_high * 100).toFixed(1) : "—";
  const verdictColor =
    bt.accuracy >= 0.70 ? "text-emerald-300 bg-emerald-950/40 border-emerald-700/50"
    : bt.accuracy >= 0.62 ? "text-emerald-200 bg-emerald-950/30 border-emerald-800/50"
    : bt.accuracy >= 0.55 ? "text-blue-200 bg-blue-950/30 border-blue-800/50"
    : "text-amber-200 bg-amber-950/30 border-amber-800/50";

  return (
    <div className="rounded-lg border border-purple-900/50 bg-gradient-to-br from-[#0a0512] via-[#0d0820] to-[#0a0512] p-3 space-y-3" data-testid="stockpimp-predicts">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="bg-gradient-to-br from-fuchsia-500 to-purple-600 rounded p-1.5">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-sm font-semibold text-white tracking-tight">StockPimp Predicts</div>
            <div className="text-[10px] text-gray-400">
              25-signal fusion · {ticker} · predicting {data.horizon_label || "next day"} · as of {data.as_of}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <select
            value={horizon}
            onChange={(e) => setHorizon(e.target.value)}
            className="bg-[#1a1030] text-fuchsia-200 border border-fuchsia-700/60 rounded px-2 py-1 text-xs font-medium"
            data-testid="sp-predicts-horizon"
            title="Prediction horizon — how far ahead the model predicts"
          >
            {[
              ["1d", "Predict: Next Day"],
              ["1w", "Predict: Next Week"],
              ["1mo", "Predict: Next Month"],
              ["3mo", "Predict: Next 3 Months"],
              ["6mo", "Predict: Next 6 Months"],
              ["1y", "Predict: Next Year"],
            ].map(([v, label]) => (
              <option key={v} value={v}>{label}</option>
            ))}
          </select>
          <select
            value={window}
            onChange={(e) => setWindow(e.target.value)}
            className="bg-[#1a1030] text-gray-200 border border-purple-900/60 rounded px-2 py-1 text-xs"
            data-testid="sp-predicts-window"
            title="Backtest window — how far back to evaluate accuracy"
          >
            {[
              ["10y", "10 Years"],
              ["5y", "5 Years"],
              ["3y", "3 Years"],
              ["2y", "2 Years"],
              ["1y", "1 Year"],
              ["6mo", "6 Months"],
              ["3mo", "3 Months"],
              ["1mo", "1 Month"],
              ["10d", "10 Days"],
              ["5d", "5 Days"],
            ].map(([v, label]) => (
              <option key={v} value={v}>{label}</option>
            ))}
          </select>
          <select
            value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))}
            className="bg-[#1a1030] text-gray-200 border border-purple-900/60 rounded px-2 py-1 text-xs"
            data-testid="sp-predicts-threshold"
          >
            {[0.10, 0.15, 0.20, 0.25, 0.30].map((t) => (
              <option key={t} value={t}>±{t.toFixed(2)} threshold</option>
            ))}
          </select>
        </div>
      </div>

      {/* Windowed Edge Comparison — durability across windows */}
      {edgeData?.windows?.length > 0 && (
        <div className="rounded border border-purple-900/40 bg-black/40 p-3" data-testid="sp-edge-comparison">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
            <div className="text-xs font-semibold text-gray-200 flex items-center gap-1.5">
              <Target className="w-3.5 h-3.5 text-fuchsia-400" />
              Windowed Edge Comparison — backtest accuracy across all 10 windows
            </div>
            {edgeData.regime && (
              <div
                className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${
                  edgeData.regime.includes("IMPROVING")
                    ? "border-emerald-700/60 bg-emerald-950/40 text-emerald-200"
                    : edgeData.regime.includes("DECAYING")
                    ? "border-red-700/60 bg-red-950/40 text-red-200"
                    : "border-blue-700/60 bg-blue-950/40 text-blue-200"
                }`}
                data-testid="sp-edge-regime"
              >
                {edgeData.regime}
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[10px] tabular-nums">
              <thead>
                <tr className="text-gray-400 border-b border-purple-900/40">
                  <th className="text-left py-1 px-1 font-medium">Window</th>
                  <th className="text-right py-1 px-1 font-medium">Bars</th>
                  <th className="text-right py-1 px-1 font-medium">Fires</th>
                  <th className="text-right py-1 px-1 font-medium">Accuracy</th>
                  <th className="text-right py-1 px-1 font-medium hidden sm:table-cell">95% CI</th>
                  <th className="text-right py-1 px-1 font-medium hidden md:table-cell">Coverage</th>
                  <th className="text-right py-1 px-1 font-medium hidden md:table-cell">UP / DOWN</th>
                  <th className="text-center py-1 px-1 font-medium">Edge</th>
                </tr>
              </thead>
              <tbody>
                {edgeData.windows.map((w) => {
                  const isActive = w.window === window;
                  const tierColor = {
                    strong: "text-emerald-300 bg-emerald-950/30",
                    solid: "text-emerald-200 bg-emerald-950/20",
                    modest: "text-blue-200 bg-blue-950/20",
                    marginal: "text-amber-300 bg-amber-950/20",
                    insufficient: "text-gray-400 bg-gray-900/30",
                    none: "text-gray-500 bg-gray-900/20",
                  }[w.tier] || "text-gray-400";
                  const accStr = w.accuracy != null ? `${(w.accuracy * 100).toFixed(1)}%` : "—";
                  const ciStr = w.ci_95_low != null
                    ? `${(w.ci_95_low * 100).toFixed(0)}–${(w.ci_95_high * 100).toFixed(0)}%`
                    : "—";
                  const upAcc = w.up_accuracy != null ? `${(w.up_accuracy * 100).toFixed(0)}%` : "—";
                  const dnAcc = w.down_accuracy != null ? `${(w.down_accuracy * 100).toFixed(0)}%` : "—";
                  return (
                    <tr
                      key={w.window}
                      onClick={() => setWindow(w.window)}
                      className={`cursor-pointer transition-colors hover:bg-purple-900/20 ${
                        isActive ? "bg-fuchsia-950/30 border-l-2 border-fuchsia-500" : ""
                      }`}
                      data-testid={`sp-edge-row-${w.window}`}
                      title="Click to switch the active window"
                    >
                      <td className="text-left py-1 px-1 font-medium text-gray-200">
                        {isActive && <span className="text-fuchsia-400">▸ </span>}
                        {w.label}
                      </td>
                      <td className="text-right py-1 px-1 text-gray-400">{w.window_bars}</td>
                      <td className="text-right py-1 px-1 text-gray-300">{w.fires}</td>
                      <td className={`text-right py-1 px-1 font-bold ${
                        w.accuracy >= 0.62 ? "text-emerald-300"
                        : w.accuracy >= 0.55 ? "text-blue-200"
                        : w.accuracy != null ? "text-amber-300"
                        : "text-gray-500"
                      }`}>
                        {accStr}
                        {w.alt_data_in_backtest && <span className="text-fuchsia-400 ml-1" title={`alt-data on ${w.augmented_count} day${w.augmented_count === 1 ? "" : "s"}`}>★</span>}
                      </td>
                      <td className="text-right py-1 px-1 text-gray-500 hidden sm:table-cell">{ciStr}</td>
                      <td className="text-right py-1 px-1 text-gray-500 hidden md:table-cell">{w.coverage_pct ?? "—"}%</td>
                      <td className="text-right py-1 px-1 text-gray-500 hidden md:table-cell">
                        <span className="text-emerald-400">{upAcc}</span>
                        <span className="text-gray-600 mx-0.5">/</span>
                        <span className="text-red-400">{dnAcc}</span>
                      </td>
                      <td className="text-center py-1 px-1">
                        <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${tierColor}`}>
                          {w.tier}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-[9px] text-gray-500 mt-1.5 px-1">
            Click any row to switch the active window. ★ = alt-data snapshots in backtest. Edge tiers: strong (≥70%) · solid (62–70%) · modest (55–62%) · marginal (&lt;55%) · insufficient (&lt;5 fires).
          </div>
        </div>
      )}

      {/* Headline prediction card */}
      <div className={`rounded-lg border bg-gradient-to-br ${dirBg} p-4`}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-1 flex items-center gap-3" data-testid="sp-predicts-direction">
            <DirIcon className={`w-12 h-12 ${dirColor}`} />
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-400">{data.horizon_label || "Tomorrow"}</div>
              <div className={`text-3xl font-bold ${dirColor}`}>{dir}</div>
              <div className="text-xs text-gray-300 mt-0.5">
                {today.conviction_pct?.toFixed(1)}% conviction
              </div>
            </div>
          </div>
          <div className="rounded bg-black/40 p-2">
            <div className="text-[10px] text-gray-400 uppercase">Composite</div>
            <div className={`text-xl font-bold ${today.composite > 0 ? "text-emerald-300" : today.composite < 0 ? "text-red-300" : "text-gray-300"}`}>
              {today.composite >= 0 ? "+" : ""}{today.composite?.toFixed(3)}
            </div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              hist {today.hist_composite >= 0 ? "+" : ""}{today.hist_composite?.toFixed(2)} · live {today.live_composite >= 0 ? "+" : ""}{today.live_composite?.toFixed(2)}
            </div>
          </div>
          <div className="rounded bg-black/40 p-2">
            <div className="text-[10px] text-gray-400 uppercase">Rules Firing</div>
            <div className="text-xl font-bold text-fuchsia-300">
              {today.rules_firing} / {today.rules_total}
            </div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              {((today.rules_firing / today.rules_total) * 100).toFixed(0)}% engaged
            </div>
          </div>
          <div className={`rounded border ${verdictColor} p-2`}>
            <div className="text-[10px] uppercase opacity-80">{data.window_label} Backtest Verdict</div>
            <div className="text-sm font-bold mt-0.5">{data.verdict}</div>
            <div className="text-[10px] mt-0.5 opacity-80">
              {accuracy}% acc · CI [{ciLow}–{ciHigh}%]
            </div>
            {bt.alt_data_in_backtest && (
              <div className="text-[10px] mt-0.5 text-fuchsia-300 font-semibold" data-testid="sp-predicts-alt-augmented-badge">
                + alt-data on {bt.augmented_count} day{bt.augmented_count === 1 ? "" : "s"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Smart-money confirmation — top-10 leaderboard politicians who bought
          this ticker in the last 30d. Pure confidence overlay (does not change
          signal weights). */}
      {data.smart_money?.confirmed && (
        <div
          className="rounded border border-amber-500/60 bg-amber-950/30 p-2 flex items-start gap-2"
          data-testid="sp-smart-money-badge"
        >
          <div className="text-amber-300 text-lg leading-none mt-0.5">📊</div>
          <div className="flex-1 text-xs">
            <div className="font-semibold text-amber-200">
              Smart-money confirmation: +{data.smart_money.top10_count}
            </div>
            <div className="text-amber-200/80 mt-0.5">
              {data.smart_money.summary.replace(/^📊\s*/, "")}
            </div>
            {data.smart_money.top_buyers?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {data.smart_money.top_buyers.map((b) => (
                  <span
                    key={b.filer}
                    className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-200"
                    title={`Last buy: ${b.last_tx_this_ticker} · ${b.n_buys_this_ticker} buy(s) · $${b.buy_mid_this_ticker.toLocaleString()}`}
                  >
                    {b.filer} +{b.weighted_pct_change.toFixed(1)}%
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Backtest stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="sp-predicts-bt-stats">
        <Stat label="Backtest Acc" value={`${accuracy}%`} accent="fuchsia" />
        <Stat label="95% CI" value={`${ciLow}–${ciHigh}%`} accent="purple" />
        <Stat label="Signal Fires" value={bt.fires?.toLocaleString() || "—"} accent="blue" />
        <Stat label="Coverage" value={`${bt.coverage_pct ?? "—"}%`} accent="cyan" />
        <Stat
          label="UP Acc"
          value={bt.up_accuracy != null ? `${(bt.up_accuracy * 100).toFixed(1)}%` : "—"}
          sub={`${bt.up_fires} fires`}
          accent="emerald"
        />
        <Stat
          label="DOWN Acc"
          value={bt.down_accuracy != null ? `${(bt.down_accuracy * 100).toFixed(1)}%` : "—"}
          sub={`${bt.down_fires} fires`}
          accent="red"
        />
      </div>

      {/* Live overlays panel */}
      <div className="rounded border border-purple-900/40 bg-black/40">
        <button
          onClick={() => setShowLive(!showLive)}
          className="w-full flex items-center justify-between px-3 py-2 hover:bg-purple-900/10 transition-colors"
          data-testid="sp-predicts-live-toggle"
        >
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-fuchsia-400" />
            <span className="text-xs font-medium text-gray-200">Live Overlays — 11 forward-looking sources (markets + alt-data)</span>
          </div>
          {showLive ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
        </button>
        {showLive && (
          <div className="px-3 pb-3 grid grid-cols-2 md:grid-cols-3 gap-2">
            <OverlayCard
              icon={BarChart3}
              title="Futures (avg)"
              value={fmtPct(ov.futures?.avg_pct)}
              detail={ov.futures?.items?.map((f) => `${f.symbol} ${fmtPct(f.pct)}`).join(" · ")}
            />
            <OverlayCard
              icon={TrendingUp}
              title="AH / PM"
              value={fmtPct(ov.ah_pm?.avg_pct)}
              detail={`AH ${fmtPct(ov.ah_pm?.ah_pct)} · PM ${fmtPct(ov.ah_pm?.pm_pct)}`}
            />
            <OverlayCard
              icon={Newspaper}
              title="News (36h)"
              value={ov.news ? (ov.news.score >= 0 ? "+" : "") + ov.news.score?.toFixed(2) : "—"}
              detail={ov.news ? `${ov.news.pos}🟢 ${ov.news.neg}🔴 ${ov.news.neu}⚪` : "no recent news"}
              valueColor={ov.news ? (ov.news.score > 0 ? "text-emerald-300" : ov.news.score < 0 ? "text-red-300" : "text-gray-300") : "text-gray-400"}
            />
            <OverlayCard
              icon={Layers}
              title="Sector Breadth"
              value={`${ov.sectors?.advancing ?? 0}↑ / ${ov.sectors?.declining ?? 0}↓`}
              detail={`avg ${fmtPct(ov.sectors?.avg_pct)} · A/D ${ov.sectors?.ad_ratio ?? "—"}`}
              valueColor="text-blue-300"
            />
            <OverlayCard
              icon={Banknote}
              title="10Y Treasury"
              value={ov.treasury?.yield_pct != null ? `${ov.treasury.yield_pct.toFixed(2)}%` : "—"}
              detail={`change ${fmtPct(ov.treasury?.change_pct)} (rising = bearish equities)`}
              valueColor={ov.treasury?.change_pct > 0 ? "text-red-300" : "text-emerald-300"}
            />
            <OverlayCard
              icon={Coins}
              title="Commodities"
              value={`GLD ${fmtPct(ov.commodities?.gld_pct)}`}
              detail={`USO ${fmtPct(ov.commodities?.uso_pct)}`}
            />
            {/* ─── Alternative-data sources ─── */}
            <OverlayCard
              icon={Vote}
              title="Polymarket"
              value={ov.polymarket?.score != null ? (ov.polymarket.score >= 0 ? "+" : "") + ov.polymarket.score.toFixed(2) : "—"}
              detail={ov.polymarket
                ? `${ov.polymarket.market_count} mkts · vol $${(ov.polymarket.total_volume || 0).toLocaleString()}`
                : "no matching prediction markets"}
              valueColor={ov.polymarket?.score > 0 ? "text-emerald-300" : ov.polymarket?.score < 0 ? "text-red-300" : "text-gray-300"}
              hover={ov.polymarket?.markets?.map((m) => `• ${m.question} → ${m.yes_price}`).join("\n")}
            />
            <OverlayCard
              icon={MessageSquare}
              title="Reddit (WSB+)"
              value={ov.reddit?.score != null ? (ov.reddit.score >= 0 ? "+" : "") + ov.reddit.score.toFixed(2) : "—"}
              detail={ov.reddit
                ? `${ov.reddit.mentions} mentions · ${ov.reddit.upvotes?.toLocaleString()} ↑ · rank #${ov.reddit.rank ?? "—"} · ${ov.reddit.momentum_pct >= 0 ? "+" : ""}${ov.reddit.momentum_pct}%`
                : "ticker not in top trending"}
              valueColor={ov.reddit?.score > 0 ? "text-emerald-300" : ov.reddit?.score < 0 ? "text-red-300" : "text-gray-300"}
            />
            <OverlayCard
              icon={FileText}
              title="Seeking Alpha"
              value={ov.seeking_alpha?.score != null ? (ov.seeking_alpha.score >= 0 ? "+" : "") + ov.seeking_alpha.score.toFixed(2) : "—"}
              detail={ov.seeking_alpha
                ? `${ov.seeking_alpha.total} articles · ${ov.seeking_alpha.pos}🟢 ${ov.seeking_alpha.neg}🔴 ${ov.seeking_alpha.neu}⚪`
                : "no recent articles"}
              valueColor={ov.seeking_alpha?.score > 0 ? "text-emerald-300" : ov.seeking_alpha?.score < 0 ? "text-red-300" : "text-gray-300"}
            />
            <OverlayCard
              icon={Landmark}
              title="Politicians (PTR)"
              value={ov.political_trades
                ? `${ov.political_trades.buys ?? 0} buys / ${ov.political_trades.sells ?? 0} sells`
                : "—"}
              detail={ov.political_trades
                ? `$${(ov.political_trades.buy_vol || 0).toLocaleString()} buy / $${(ov.political_trades.sell_vol || 0).toLocaleString()} sell`
                : "no congressional trades in 60d (PDF parser running on backend)"}
              valueColor={ov.political_trades?.score > 0 ? "text-emerald-300" : ov.political_trades?.score < 0 ? "text-red-300" : "text-gray-300"}
            />
            <OverlayCard
              icon={Banknote}
              title="OGE 278-T"
              value={ov.oge_278t?.hits != null ? `${ov.oge_278t.hits} hits` : "—"}
              detail={ov.oge_278t?.note
                ? ov.oge_278t.note.slice(0, 70)
                : "exec branch — PDF source"}
              valueColor="text-gray-400"
            />
          </div>
        )}

        {/* Top scorers who bought this ticker — actionable per-ticker
            companion to the Politicians (PTR) card above. */}
        {ticker && <PoliticianBuyersWidget ticker={ticker} />}
      </div>

      {/* Signal contribution chart */}
      <div className="rounded bg-black/40 p-3 border border-purple-900/30">
        <div className="text-xs font-semibold text-gray-200 mb-2 flex items-center gap-1.5">
          <Target className="w-3.5 h-3.5 text-fuchsia-400" />
          Today's Signal Breakdown — 14 Historical Rules
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={histChart} margin={{ top: 5, right: 10, bottom: 35, left: -15 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" />
            <XAxis
              dataKey="name"
              tick={{ fill: "#888", fontSize: 9 }}
              angle={-30}
              textAnchor="end"
              height={50}
              interval={0}
            />
            <YAxis tick={{ fill: "#888", fontSize: 10 }} domain={[-1, 1]} />
            <Tooltip
              contentStyle={{ background: "#0d0820", border: "1px solid #5b21b6", fontSize: 11 }}
              formatter={(v) => [v.toFixed(3), "Score"]}
            />
            <ReferenceLine y={0} stroke="#666" />
            <Bar dataKey="score" radius={[3, 3, 0, 0]}>
              {histChart.map((d, i) => (
                <Cell key={i} fill={d.score > 0.05 ? "#10b981" : d.score < -0.05 ? "#ef4444" : "#6b7280"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Per-rule win rate (toggle) */}
      <div className="rounded bg-black/40 border border-purple-900/30">
        <button
          onClick={() => setShowRules(!showRules)}
          className="w-full flex items-center justify-between px-3 py-2 hover:bg-purple-900/10 transition-colors"
          data-testid="sp-predicts-rules-toggle"
        >
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-fuchsia-400" />
            <span className="text-xs font-medium text-gray-200">Per-Rule Win Rate Over {data.window_label}</span>
          </div>
          {showRules ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
        </button>
        {showRules && (
          <div className="p-3 space-y-1">
            {(bt.rule_table || []).map((r) => {
              const wr = r.win_rate;
              const wrPct = wr != null ? (wr * 100).toFixed(1) : "—";
              const color = wr >= 0.65 ? "bg-emerald-500"
                : wr >= 0.55 ? "bg-blue-500"
                : wr >= 0.45 ? "bg-amber-500"
                : "bg-red-500";
              return (
                <div key={r.key} className="flex items-center gap-2 text-xs" data-testid={`sp-rule-${r.key}`}>
                  <div className="w-44 text-gray-300 truncate">{r.label}</div>
                  <div className="flex-1 h-3 bg-gray-800 rounded overflow-hidden">
                    {wr != null && (
                      <div
                        className={`h-full ${color} transition-all`}
                        style={{ width: `${Math.min(100, wr * 100)}%` }}
                      />
                    )}
                  </div>
                  <div className="w-12 text-right text-gray-200 font-mono">{wrPct}%</div>
                  <div className="w-14 text-right text-gray-500 text-[10px]">{r.fires} fires</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Last 60 fires */}
      {bt.history?.length > 0 && (
        <div className="rounded bg-black/40 p-3 border border-purple-900/30">
          <div className="text-xs font-semibold text-gray-200 mb-2 flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5 text-fuchsia-400" />
            Last 60 Signal Fires (composite height = conviction)
          </div>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={histAcc} margin={{ top: 5, right: 10, bottom: 5, left: -25 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="idx" tick={{ fill: "#888", fontSize: 9 }} />
              <YAxis tick={{ fill: "#888", fontSize: 10 }} domain={[-1, 1]} />
              <Tooltip
                contentStyle={{ background: "#0d0820", border: "1px solid #5b21b6", fontSize: 11 }}
                formatter={(v, n, p) => [`${v} (${p?.payload?.direction})`, p?.payload?.correct ? "✅ correct" : "❌ wrong"]}
                labelFormatter={(l, p) => p?.[0]?.payload?.label || `#${l}`}
              />
              <ReferenceLine y={0} stroke="#666" />
              <Bar dataKey="prob" radius={[2, 2, 0, 0]}>
                {histAcc.map((d, i) => (
                  <Cell key={i} fill={d.correct ? "#10b981" : "#ef4444"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Methodology footer */}
      <div className="text-[10px] text-gray-500 leading-relaxed px-1">
        <strong className="text-fuchsia-300">Methodology:</strong> 14 historical signals (RSI+MFI, SMA align, Bollinger,
        200-SMA, VIX, money flow, payday, candlestick, streak, gap, pullback, RSI(2), volume spike, divergence) are
        walk-forward backtested over the selected window ({data.window_label}) of {ticker} daily bars. The 11 live overlays
        (futures, AH/PM, news, sectors, 10Y treasury, commodities, Polymarket, Reddit, Seeking Alpha, politician trades, OGE 278-T) add forward-looking context at 35% authority.
        A signal fires only when the combined composite exceeds ±{threshold}. Verdict above ≥55% is a meaningful
        edge over base rate.
      </div>
      {/* Alt-data backtest status — switches between informational disclosure and active confirmation */}
      {bt.alt_data_in_backtest ? (
        <div className="text-[10px] leading-relaxed px-3 py-2 mt-2 rounded border border-emerald-700/50 bg-emerald-950/30 text-emerald-100" data-testid="sp-alt-backtest-active">
          <strong className="text-emerald-300">✓ Alt-data backtest active —</strong> {bt.augmented_count} day{bt.augmented_count === 1 ? "" : "s"} in this {data.window_label} window have stored alt-data snapshots. For those days, the historical signals (65%) were blended with the captured Polymarket / Reddit / Seeking Alpha / politician-trade / OGE scores (35%) before the fire decision — a true forward-collected backtest of the alt-data layer.
        </div>
      ) : (
        <div className="text-[10px] leading-relaxed px-3 py-2 mt-2 rounded border border-amber-700/50 bg-amber-950/30 text-amber-200/90" data-testid="sp-alt-backtest-inactive">
          <strong className="text-amber-300">Alt-data not yet in backtest —</strong> Daily snapshots of the 5 alt-data sources (Polymarket, Reddit/ApeWisdom, Seeking Alpha, politician trades, OGE 278-T) are captured each time {ticker} is queried, but the selected {data.window_label} window does not yet contain enough completed snapshots (with verified next-day actuals) to augment the backtest. Snapshots accumulate forward starting now — once the window covers dates with stored snapshots, those days will automatically blend hist+live composites for honest alt-data validation. Long windows (≥1y) remain historical-only by design.
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, sub, accent = "fuchsia" }) => {
  const tone = {
    fuchsia: "border-fuchsia-800/50 text-fuchsia-200",
    purple: "border-purple-800/50 text-purple-200",
    blue: "border-blue-800/50 text-blue-200",
    cyan: "border-cyan-800/50 text-cyan-200",
    emerald: "border-emerald-800/50 text-emerald-200",
    red: "border-red-800/50 text-red-200",
  }[accent];
  return (
    <div className={`rounded border ${tone} bg-black/40 p-2`}>
      <div className="text-[9px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-sm font-bold">{value}</div>
      {sub && <div className="text-[9px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
};

const OverlayCard = ({ icon: Icon, title, value, detail, valueColor, hover }) => (
  <div className="rounded bg-black/40 border border-purple-900/30 p-2" title={hover || undefined}>
    <div className="flex items-center gap-1.5 mb-0.5">
      <Icon className="w-3 h-3 text-fuchsia-400" />
      <div className="text-[10px] uppercase tracking-wider text-gray-400">{title}</div>
    </div>
    <div className={`text-sm font-bold ${valueColor || (typeof value === "string" && value.startsWith("+") ? "text-emerald-300" : value?.startsWith?.("-") ? "text-red-300" : "text-gray-200")}`}>
      {value}
    </div>
    {detail && <div className="text-[9px] text-gray-500 mt-0.5 truncate" title={detail}>{detail}</div>}
  </div>
);

const fmtPct = (v) => {
  if (v == null || isNaN(v)) return "—";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(2)}%`;
};
